'use client'

import type { LawPG } from '@shared/db/schema'
import { Row } from '@christiankaindl/lyts'
import * as styles from './Title.css'
import { ClipboardCheck } from 'lucide-react'
import Button from '@shared/components/Button/Button'
import { useCopyToClipboard } from '@shared/hooks/useCopyToClipboard'
import { domain, formatNumber, getUrl } from '@shared/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shared/components/Tooltip/Tooltip'
import { HovercardProvider, HovercardAnchor, Hovercard } from '@ariakit/react'
import Link from 'next/link'

export function HoverOptions({
  children,
  enumeration,
  counts,
  law,
  clauseSlug,
}: {
  children: React.ReactNode
  enumeration: string
  counts?: Record<string, number>
  law: LawPG
  clauseSlug?: string
}) {
  const urlOpts = {
    norm: law.abbreviation || law.normId,
    normId: law.normId,
    clauseSlug,
    enumeration,
  }
  const clauseHref = getUrl(law.lawType ?? '', urlOpts)

  return (
    <HovercardProvider placement='bottom-start' showTimeout={0} hideTimeout={0}>
      <HovercardAnchor render={<Link href={clauseHref} />}>
        {children}
      </HovercardAnchor>
      <Hovercard unmountOnHide className={styles.hoverMenu}>
        <Row gap={0.25}>
          <CopyButton enumeration={enumeration} />
          {counts?.decision && counts.decision > 0 && (
            <Button
              variant='flat'
              size='tiny'
              circular
              href={`${clauseHref}#rueckverweise`}
              linkProps={{ prefetch: false }}
            >
              <span>
                {formatNumber(counts.decision)} Entscheidung
                {counts.decision > 1 ? 'en' : ''}
              </span>
            </Button>
          )}
          {counts?.clause && counts.clause > 0 && (
            <Button
              variant='flat'
              size='tiny'
              circular
              href={`${clauseHref}#rueckverweise`}
              linkProps={{ prefetch: false }}
            >
              <span>
                {formatNumber(counts.clause)} Paragraf
                {counts.clause > 1 ? 'en' : ''}
              </span>
            </Button>
          )}
          {counts?.rechtssatz && counts.rechtssatz > 0 && (
            <Button
              variant='flat'
              size='tiny'
              circular
              href={`${clauseHref}#rueckverweise`}
              linkProps={{ prefetch: false }}
            >
              <span>
                {formatNumber(counts.rechtssatz)} Rechtss
                {counts.rechtssatz > 1 ? 'ätze' : 'atz'}
              </span>
            </Button>
          )}
        </Row>
      </Hovercard>
    </HovercardProvider>
  )
}

function CopyButton({ enumeration }: { enumeration: string }) {
  const { copied, copyText } = useCopyToClipboard()
  function handleCopy() {
    copyText(`${domain}${window.location.pathname}#${enumeration}`).catch(
      console.error,
    )
  }
  return (
    <Tooltip placement='bottom'>
      <TooltipTrigger
        render={
          <Button
            onClick={handleCopy}
            square
            variant='flat'
            size='tiny'
            circular
          />
        }
      >
        {!copied && (
          <svg>
            <use xlinkHref='/assets/sprite.svg#link' />
          </svg>
        )}
        {copied && <ClipboardCheck size={16} />}
      </TooltipTrigger>
      <TooltipContent>Link kopieren</TooltipContent>
    </Tooltip>
  )
}
